import gql from 'graphql-tag';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Mutation} from 'react-apollo';
import {Button, Container} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import Error from '../../../common/components/Error/Error';
import PopIn from '../../../common/components/PopIn';
import {
  CancelButton,
  FooterRow,
  GradientCol,
  TitleButton,
} from '../../../common/components/StyledComp';
import {buildFormToSubmit, formStateWithData, initFormState} from '../../../utils/form';
import FormInputFactory from '../../../common/components/layout/FormInputFactory';
import InputCol from '../../../common/components/layout/InputCol';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBell} from '@fortawesome/free-solid-svg-icons/faBell';

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  width: auto;
`;

const GRAPHQL_MUTATION_ADD_NOTIFICATIONS_CREDIT = gql`
  mutation addMerchantNotificationsCredit($merchantId: ID!, $quantity: Int!) {
    addMerchantNotificationsCredit(merchantId: $merchantId, quantity: $quantity) {
      id
      totalAvailableNotification
    }
  }
`;


const formFactory = {
  merchantId: {
    initial: 0,
    fromData: 'id',
    toSubmit: 'merchantId',
    required: true,
    type: 'number',
  },
  quantity: {
    initial: 1,
    toSubmit: 'quantity',
    required: true,
    type: 'number',
  },
};


const initialNewNotificationCredit = initFormState(formFactory);

const CreditNotificationMerchant = ({t, id, totalAvailableNotification}) => {

  const [modalVisible, setModalVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newNotificationCredit, setNewNotificationCredit] = useState(initialNewNotificationCredit);

  const setState = (name, value) => {
    setNewNotificationCredit({
      ...newNotificationCredit,
      [name]: +value,
    });
  };

  useEffect(() => {
    const newStateFilled = formStateWithData({id}, formFactory);
    setNewNotificationCredit(newStateFilled);
  }, [id, totalAvailableNotification]);

  const resetCreditNotification = () => {
    setModalVisible(false);
    setSubmitted(false);
  };

  const onNotificationCredited = (creditNewNotification, e) => {
    e.preventDefault();
    setSubmitted(true);
    if (newNotificationCredit.quantity > 0) {
      const variables = buildFormToSubmit(formFactory, newNotificationCredit);
      creditNewNotification({
        variables,
      })
        .then(({data}) => {
          resetCreditNotification();
        }).catch(err =>{
          console.error(err);
        });
    }
  };

  const finalQuantity = newNotificationCredit.quantity + totalAvailableNotification;
  return (
    <Mutation
      mutation={GRAPHQL_MUTATION_ADD_NOTIFICATIONS_CREDIT}
      refetchQueries={['merchants', 'merchantsSelect']}
    >
      {(mutation, {error, loading}) => {
        return (
          <div>

            <Button className="btn-update" onClick={() => setModalVisible(true)}>
              <FontAwesomeIcon icon={faBell} color="#329aef" size="sm"/>
            </Button>

            <PopIn visible={modalVisible} setModalVisible={setModalVisible} title={t('creditNotification.title')}>
              <Container>
                <form
                  onSubmit={e => {
                    onNotificationCredited(mutation, e);
                  }}
                >
                  <InputCol
                    title={t('totalAvailableNotification')}
                    name="total"
                    type="number"
                    value={totalAvailableNotification}
                    disabled
                  />
                  <FormInputFactory
                    title={t('quantity')}
                    nameInput="quantity"
                    formFactory={formFactory}
                    formState={newNotificationCredit}
                    onChange={setState}
                    show={submitted && (!newNotificationCredit.quantity || newNotificationCredit.quantity <= 0)}
                    min={1}
                  />
                  <InputCol
                    title={t('totalQuantity')}
                    name="total"
                    type="number"
                    value={finalQuantity}
                    disabled
                  />
                  <FooterRow>
                    <GradientCol sm={{span: 6, offset: 3}}>
                      {error && submitted && (
                        <Error error={t('creditNotification.error')}/>
                      )}
                      <div className="popin-buttons">
                        <CancelButtonForm
                          onClick={() => {
                            resetCreditNotification();
                          }}>
                          {t('creditNotification.cancel')}
                        </CancelButtonForm>
                        <TitleButton
                          type="submit"
                          disabled={loading}
                          onClick={() => {
                            setSubmitted(true);
                          }}
                        >
                          {t('creditNotification.add')}
                        </TitleButton>
                      </div>
                    </GradientCol>
                  </FooterRow>
                </form>
              </Container>
            </PopIn>
          </div>
        );
      }}
    </Mutation>
  );
};

CreditNotificationMerchant.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  totalAvailableNotification: PropTypes.number.isRequired,
};

export default compose(
  withTranslation('merchant'),
)(CreditNotificationMerchant);
